class BundleVisualElement extends HTMLElement {
  constructor() {
    super();
    // Product data structure:
    // { ...productData, gridSize: number, position: { row: number, col: number } }
    this.products = [];
    this.gridState = []; // 2D array tracking occupied cells
    this.closedBoxes = []; // Array of completed boxes: { id, products, gridState }
    this.isLoading = false;
    this.isPanelOpen = false;
    this.discountThresholds = [];
    this.discountMaxMessage = '';
  }

  connectedCallback() {
    this.bundleType = this.dataset.bundleType || 'fill-your-box';
    this.bundleName = this.dataset.bundleName || 'Fill Your Box';
    this.minSpots = parseInt(this.dataset.minSpots) || 4;
    this.maxSpots = parseInt(this.dataset.maxSpots) || 8;
    this.gridColumns = parseInt(this.dataset.gridColumns) || 4;
    this.gridRows = parseInt(this.dataset.gridRows) || 2;
    this.bundleHandle = this.dataset.bundleHandle || '';
    this.storageKey = `bundle_visual_${this.bundleType}_${this.bundleHandle}`;
    this.bundleId = this.generateBundleId();

    // Parse discount thresholds from JSON (passed from Liquid blocks)
    const thresholdsData = this.dataset.discountThresholds;
    if (thresholdsData) {
      try {
        this.discountThresholds = JSON.parse(thresholdsData)
          .sort((a, b) => a.itemCount - b.itemCount);
      } catch (e) {
        this.discountThresholds = [];
      }
    }
    this.discountMaxMessage = this.dataset.discountMaxMessage || '';

    this.initializeGrid();
    this.cacheElements();
    this.bindEvents();
    this.loadFromStorage();
    this.initMobileSheet();
    this.updateUI();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  initializeGrid() {
    // Initialize empty grid state
    this.gridState = [];
    for (let row = 0; row < this.gridRows; row++) {
      this.gridState[row] = [];
      for (let col = 0; col < this.gridColumns; col++) {
        this.gridState[row][col] = null;
      }
    }
  }

  cacheElements() {
    this.gridContainer = this.querySelector('[data-grid]');
    this.itemsLayer = this.querySelector('[data-items-layer]');
    this.spotElements = this.querySelectorAll('[data-spot-index]');
    this.productCards = this.querySelectorAll('[data-bundle-product]');
    this.progressText = this.querySelector('[data-progress-text]');
    this.progressIcons = this.querySelectorAll('[data-progress-icon-index]');
    this.totalPrice = this.querySelector('[data-total-price]');
    this.savingsElement = this.querySelector('[data-savings]');
    this.addToCartBtn = this.querySelector('[data-add-bundle]');
    this.clearBtn = this.querySelector('[data-clear-bundle]');
    this.anotherBoxContainer = this.querySelector('[data-another-box]');
    this.anotherBoxBtn = this.querySelector('[data-add-another]');
    this.closedBoxesContainer = this.querySelector('[data-closed-boxes]');
    this.boxNumberSpan = this.querySelector('[data-box-number]');
    this.filterBtns = this.querySelectorAll('[data-filter]');

    // Product detail modal elements
    const root = this.closest('.bundle-visual--root');
    this.productModal = root?.querySelector('[data-product-modal]');
    this.modalImages = root?.querySelector('[data-modal-images]');
    this.modalTitle = root?.querySelector('[data-modal-title]');
    this.modalPrice = root?.querySelector('[data-modal-price]');
    this.modalDescription = root?.querySelector('[data-modal-description]');
    this.modalAddBtn = root?.querySelector('[data-modal-add]');
    this.modalCloseBtn = root?.querySelector('[data-modal-close]');

    // Discount incentive elements
    this.discountIncentiveContainer = this.querySelector('[data-discount-incentive]');
    this.discountText = this.querySelector('[data-discount-text]');

    // Mobile elements
    if (root) {
      this.mobileSheet = root.querySelector('[data-mobile-sheet]');
      this.mobileBar = root.querySelector('[data-mobile-bar]');
      this.mobilePanel = root.querySelector('[data-mobile-panel]');
      this.mobilePreview = root.querySelector('[data-mobile-preview]');
      this.mobileSpots = root.querySelectorAll('[data-mobile-spot]');
      this.mobileCount = root.querySelector('[data-mobile-count]');
      this.mobilePrice = root.querySelector('[data-mobile-price]');
      this.mobileBox = root.querySelector('[data-mobile-box]');
      this.mobileGrid = root.querySelector('[data-mobile-grid]');
      this.mobileItemsLayer = root.querySelector('[data-mobile-items-layer]');
      this.mobileItemsList = root.querySelector('[data-mobile-items-list]');
      this.mobileProgressText = root.querySelector('[data-mobile-progress-text]');
      this.mobileTotalPrice = root.querySelector('[data-mobile-total-price]');
      this.mobileSavings = root.querySelector('[data-mobile-savings]');
      this.mobileAddBtn = root.querySelector('[data-mobile-add-bundle]');
      this.mobileCloseBtn = root.querySelector('[data-close-panel]');
      // Mobile discount elements
      this.mobileDiscount = root.querySelector('[data-mobile-discount]');
      this.mobilePanelDiscount = root.querySelector('[data-mobile-panel-discount]');
      this.mobileToastEl = root.querySelector('[data-bundle-toast-mobile]');
    }

    this.toastEl = this.querySelector('[data-bundle-toast]');
  }

  bindEvents() {
    // Product card clicks - image/title opens modal, "Add to Box" button adds to bundle
    this.productCards.forEach(card => {
      // Image and title click -> open modal
      const detailTriggers = card.querySelectorAll('[data-product-detail-trigger]');
      detailTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          this.openProductModal(card);
        });
      });

      // "Add to Box" button click -> add to bundle
      const addBtn = card.querySelector('.bundle-visual--product-add');
      if (addBtn) {
        addBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (card.dataset.unavailable === 'true' || card.dataset.available !== 'true') return;
          if (card.dataset.cantFit === 'true') return;
          const productData = this.getProductDataFromCard(card);
          this.addToBundle(productData);
        });
      }

      // Card click (fallback) -> add to bundle
      card.addEventListener('click', () => {
        if (card.dataset.unavailable === 'true' || card.dataset.available !== 'true') return;
        if (card.dataset.cantFit === 'true') return;
        const productData = this.getProductDataFromCard(card);
        this.addToBundle(productData);
      });
    });

    // Modal close
    if (this.modalCloseBtn) {
      this.modalCloseBtn.addEventListener('click', () => this.closeProductModal());
    }
    if (this.productModal) {
      this.productModal.addEventListener('click', (e) => {
        if (e.target === this.productModal) this.closeProductModal();
      });
      this.productModal.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.closeProductModal();
      });
    }

    // Add to cart button
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => this.addBundleToCart());
    }

    // Clear button
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => this.clearBundle());
    }

    // Add another box button
    if (this.anotherBoxBtn) {
      this.anotherBoxBtn.addEventListener('click', () => this.addAnotherBox());
    }

    // Filter buttons
    this.filterBtns.forEach(btn => {
      btn.addEventListener('click', () => this.filterProducts(btn.dataset.filter));
    });
  }

  unbindEvents() {}

  generateBundleId() {
    return `${this.bundleType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  getProductDataFromCard(card) {
    return {
      id: card.dataset.variantId,
      productId: card.dataset.productId,
      handle: card.dataset.handle,
      title: card.dataset.title,
      variantTitle: card.dataset.variantTitle,
      price: parseFloat(card.dataset.price) || 0,
      image: card.dataset.image,
      available: card.dataset.available === 'true',
      gridSize: parseInt(card.dataset.gridSize) || 1
    };
  }

  // Find a position in a given grid state (pure helper — no side effects)
  _findPositionIn(state, gridSize) {
    if (gridSize === 4) {
      for (let row = 0; row <= this.gridRows - 2; row++) {
        for (let col = 0; col <= this.gridColumns - 2; col++) {
          if (state[row][col] === null &&
              state[row][col + 1] === null &&
              state[row + 1][col] === null &&
              state[row + 1][col + 1] === null) {
            return { row, col };
          }
        }
      }
      return null;
    }

    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col <= this.gridColumns - gridSize; col++) {
        let canFit = true;
        for (let i = 0; i < gridSize; i++) {
          if (state[row][col + i] !== null) {
            canFit = false;
            break;
          }
        }
        if (canFit) {
          return { row, col };
        }
      }
    }

    return null;
  }

  _markCells(state, pos, gridSize, id) {
    if (gridSize === 4) {
      state[pos.row][pos.col] = id;
      state[pos.row][pos.col + 1] = id;
      state[pos.row + 1][pos.col] = id;
      state[pos.row + 1][pos.col + 1] = id;
    } else {
      for (let i = 0; i < gridSize; i++) {
        state[pos.row][pos.col + i] = id;
      }
    }
  }

  // Pack the given products into a fresh grid. Larger items placed first.
  // Returns { state, positions } on success or { failed: true } if any item doesn't fit.
  repack(products) {
    const state = [];
    for (let row = 0; row < this.gridRows; row++) {
      state[row] = [];
      for (let col = 0; col < this.gridColumns; col++) {
        state[row][col] = null;
      }
    }
    const positions = new Map();
    const order = products.map((p, i) => ({ p, i }));
    order.sort((a, b) => (b.p.gridSize - a.p.gridSize) || (a.i - b.i));
    for (const { p } of order) {
      const pos = this._findPositionIn(state, p.gridSize);
      if (!pos) return { failed: true };
      this._markCells(state, pos, p.gridSize, p.internalId);
      positions.set(p.internalId, pos);
    }
    return { state, positions };
  }

  _applyPack(packed) {
    this.gridState = packed.state;
    this.products.forEach(p => {
      const pos = packed.positions.get(p.internalId);
      if (pos) p.position = pos;
    });
  }

  canFit(gridSize) {
    const probe = { internalId: '__probe__', gridSize };
    return !this.repack([...this.products, probe]).failed;
  }

  // Get total cells used
  getTotalSpotsUsed() {
    return this.products.reduce((sum, p) => sum + p.gridSize, 0);
  }

  showToast(message, duration = 3500) {
    clearTimeout(this._toastTimer);
    const els = [this.toastEl, this.mobileToastEl].filter(Boolean);
    els.forEach(el => {
      el.textContent = message;
      el.classList.add('is-visible');
    });
    this._toastTimer = setTimeout(() => {
      els.forEach(el => el.classList.remove('is-visible'));
    }, duration);
  }

  addToBundle(productData) {
    if (!productData.available) {
      this.showToast(`${productData.title || 'This item'} is sold out`);
      return;
    }

    const gridSize = productData.gridSize;
    const candidate = {
      ...productData,
      internalId: `product-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    const next = [...this.products, candidate];
    const packed = this.repack(next);

    if (packed.failed) {
      const spotsLeft = this.maxSpots - this.getTotalSpotsUsed();
      const title = productData.title || 'This item';
      if (gridSize > spotsLeft) {
        this.showToast(`${title} needs ${gridSize} spot${gridSize > 1 ? 's' : ''} but only ${spotsLeft} left — remove an item to make room`);
      } else {
        this.showToast(`Can't fit ${title} in the remaining layout — remove an item to make room`);
      }
      return;
    }

    this.products = next;
    this._applyPack(packed);

    this.saveToStorage();
    this.updateUI();
    this.dispatchEvent(new CustomEvent('bundle:updated', { bubbles: true, detail: { products: this.products } }));
  }

  removeProduct(internalId) {
    const next = this.products.filter(p => p.internalId !== internalId);
    if (next.length === this.products.length) return;

    const packed = this.repack(next);
    this.products = next;
    if (packed.failed) {
      this.initializeGrid();
    } else {
      this._applyPack(packed);
    }

    this.saveToStorage();
    this.updateUI();
  }

  clearBundle() {
    this.products = [];
    this.closedBoxes = [];
    this.initializeGrid();
    this.bundleId = this.generateBundleId();
    this.clearStorage();
    this.renderClosedBoxes();
    this.updateBoxNumber();
    this.updateUI();
  }

  filterProducts(filter) {
    this.filterBtns.forEach(btn => {
      btn.dataset.active = btn.dataset.filter === filter ? 'true' : 'false';
    });

    this.productCards.forEach(card => {
      if (filter === 'all') {
        card.style.display = '';
      } else {
        const tags = (card.dataset.tags || '').toLowerCase();
        const type = (card.dataset.productType || '').toLowerCase();
        const matchesFilter = tags.includes(filter.toLowerCase()) || type.includes(filter.toLowerCase());
        card.style.display = matchesFilter ? '' : 'none';
      }
    });
  }

  updateUI() {
    this.renderItems();
    this.updateSpots();
    this.updateProgress();
    this.updatePrice();
    this.updateButtons();
    this.updateProductCards();
    this.updateDiscountIncentive();
    this.updateMobileUI();
  }

  renderItems() {
    if (!this.itemsLayer) return;

    // Clear existing items
    this.itemsLayer.innerHTML = '';

    // Render each product
    this.products.forEach(product => {
      const item = document.createElement('div');
      item.className = 'bundle-visual--item';
      item.dataset.internalId = product.internalId;
      item.dataset.size = product.gridSize;

      // Position in grid
      const row = product.position.row + 1;
      const colStart = product.position.col + 1;

      if (product.gridSize === 4) {
        // 2x2 block
        item.style.gridRow = `${row} / span 2`;
        item.style.gridColumn = `${colStart} / span 2`;
      } else {
        // Single row items
        item.style.gridRow = row;
        item.style.gridColumn = `${colStart} / span ${product.gridSize}`;
      }

      item.innerHTML = `
        ${product.image ? `<img class="bundle-visual--item-image" src="${product.image}" alt="${product.title}">` : ''}
        <button type="button" class="bundle-visual--item-remove" data-remove="${product.internalId}" aria-label="Remove ${product.title}">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div class="bundle-visual--item-title">${product.title}</div>
      `;

      // Bind remove event
      const removeBtn = item.querySelector('[data-remove]');
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeProduct(product.internalId);
      });

      this.itemsLayer.appendChild(item);
    });
  }

  updateSpots() {
    this.spotElements.forEach(spot => {
      const row = parseInt(spot.dataset.row);
      const col = parseInt(spot.dataset.col);
      const isOccupied = this.gridState[row] && this.gridState[row][col] !== null;
      spot.dataset.occupied = isOccupied ? 'true' : 'false';
    });
  }

  updateProgress() {
    const spotsUsed = this.getTotalSpotsUsed();
    const meetsMinimum = spotsUsed >= this.minSpots;

    if (this.progressText) {
      if (spotsUsed === 0) {
        this.progressText.textContent = `Fill at least ${this.minSpots} spots (${this.maxSpots} total)`;
      } else if (meetsMinimum) {
        const remaining = this.maxSpots - spotsUsed;
        this.progressText.textContent = `${spotsUsed} spots filled${remaining > 0 ? ` (${remaining} remaining)` : ' - Box full!'}`;
      } else {
        this.progressText.textContent = `${spotsUsed} of ${this.minSpots} minimum spots filled`;
      }
    }

    // Update progress icons
    if (this.progressIcons) {
      this.progressIcons.forEach(icon => {
        const index = parseInt(icon.dataset.progressIconIndex);
        icon.dataset.filled = index <= spotsUsed ? 'true' : 'false';
      });
    }
  }

  getDiscountedTotals() {
    const dt = this.discountThresholds || [];
    let fullTotal = 0, totalDiscount = 0;
    const calcBox = (products) => {
      products.forEach((p, i) => {
        fullTotal += p.price;
        const t = dt.find(t => t.itemCount === i + 1);
        if (t) totalDiscount += parseFloat(t.discountAmount) || 0;
      });
    };
    this.closedBoxes.forEach(box => calcBox(box.products));
    calcBox(this.products);
    return { fullTotal, totalDiscount, discountedTotal: fullTotal - totalDiscount };
  }

  updatePrice() {
    const { totalDiscount, discountedTotal } = this.getDiscountedTotals();
    if (this.totalPrice) {
      this.totalPrice.textContent = this.formatMoney(discountedTotal * 100);
    }
    if (this.savingsElement) {
      if (totalDiscount > 0) {
        this.savingsElement.textContent = `You save ${this.formatMoney(totalDiscount * 100)}`;
        this.savingsElement.style.display = '';
      } else {
        this.savingsElement.style.display = 'none';
      }
    }
  }

  updateButtons() {
    const cellsUsed = this.getTotalSpotsUsed();
    const currentBoxMeetsMinimum = cellsUsed >= this.minSpots;
    const isFull = cellsUsed >= this.maxSpots;
    const hasClosedBoxes = this.closedBoxes.length > 0;
    const canAddToCart = hasClosedBoxes || currentBoxMeetsMinimum;

    // Count total items across all boxes
    const closedBoxItems = this.closedBoxes.reduce((sum, box) => sum + box.products.length, 0);
    const totalItems = closedBoxItems + this.products.length;
    const totalBoxes = this.closedBoxes.length + (this.products.length > 0 ? 1 : 0);

    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !canAddToCart;
      if (canAddToCart) {
        const boxText = totalBoxes === 1 ? 'box' : 'boxes';
        this.addToCartBtn.textContent = `Add ${totalBoxes} ${boxText} to Cart (${totalItems} items)`;
      } else {
        const cellsNeeded = this.minSpots - cellsUsed;
        this.addToCartBtn.textContent = `Fill ${cellsNeeded} more spot${cellsNeeded !== 1 ? 's' : ''}`;
      }
    }

    // Show "Add Another Box" when current box is full
    if (this.anotherBoxContainer) {
      this.anotherBoxContainer.style.display = isFull ? 'block' : 'none';
    }
  }

  updateProductCards() {
    this.productCards.forEach(card => {
      const variantId = card.dataset.variantId;
      const gridSize = parseInt(card.dataset.gridSize) || 1;
      const isInBundle = this.products.some(p => p.id === variantId);
      const canFitNow = this.canFit(gridSize);

      card.dataset.selected = isInBundle ? 'true' : 'false';
      card.dataset.cantFit = !canFitNow && !isInBundle ? 'true' : 'false';

      // Update button text
      const addBtn = card.querySelector('.bundle-visual--product-add');
      if (addBtn) {
        if (!canFitNow && !isInBundle) {
          addBtn.textContent = "Won't fit";
        } else if (card.dataset.available !== 'true') {
          addBtn.textContent = 'Sold Out';
        } else {
          addBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add to Box
          `;
        }
      }
    });
  }

  updateDiscountIncentive() {
    if (!this.discountThresholds.length) return;
    const closedBoxItems = this.closedBoxes.reduce((sum, box) => sum + box.products.length, 0);
    const totalItems = closedBoxItems + this.products.length;
    // Find a threshold whose message should show now
    // messageAt > 0: show when totalItems === messageAt
    // messageAt === 0 (auto): show when totalItems === itemCount - 1
    const activeT = this.discountThresholds.find(t => {
      const showAt = t.messageAt > 0 ? t.messageAt : t.itemCount - 1;
      return totalItems >= showAt && totalItems < t.itemCount;
    });
    const maxT = this.discountThresholds[this.discountThresholds.length - 1];
    let message = '';
    if (maxT && totalItems >= maxT.itemCount && this.dataset.discountMaxMessage) {
      message = this.dataset.discountMaxMessage;
    } else if (activeT) {
      message = activeT.message;
    }
    this.updateDiscountDisplay(message);
  }

  updateDiscountDisplay(message) {
    const hasMessage = message && message.length > 0;

    // Desktop discount incentive container
    if (this.discountIncentiveContainer && this.discountText) {
      if (hasMessage) {
        const currentText = this.discountText.textContent || '';
        if (currentText !== message) {
          // Animate on message change
          this.discountIncentiveContainer.dataset.animating = 'true';
          setTimeout(() => {
            this.discountIncentiveContainer.dataset.animating = 'false';
          }, 400);
        }
        this.discountText.textContent = message;
        this.discountIncentiveContainer.style.display = '';
      } else {
        this.discountIncentiveContainer.style.display = 'none';
      }
    }

    // Mobile bar discount
    if (this.mobileDiscount) {
      if (hasMessage) {
        this.mobileDiscount.textContent = message;
        this.mobileDiscount.style.display = '';
      } else {
        this.mobileDiscount.style.display = 'none';
      }
    }

    // Mobile panel discount
    if (this.mobilePanelDiscount) {
      if (hasMessage) {
        this.mobilePanelDiscount.textContent = message;
        this.mobilePanelDiscount.style.display = '';
      } else {
        this.mobilePanelDiscount.style.display = 'none';
      }
    }
  }

  openProductModal(card) {
    if (!this.productModal) return;

    const title = card.dataset.title || '';
    const price = parseFloat(card.dataset.price) || 0;
    const description = card.dataset.fullDescription || card.dataset.description || '';
    const isAvailable = card.dataset.available === 'true';
    const cantFit = card.dataset.cantFit === 'true';
    let images = [];

    try {
      images = JSON.parse(card.dataset.images || '[]');
    } catch (e) {
      const img = card.dataset.image;
      if (img) images = [img];
    }

    // Populate modal
    if (this.modalTitle) this.modalTitle.textContent = title;
    if (this.modalPrice) {
      const comparePrice = card.querySelector('.bundle-visual--product-price s');
      const priceText = this.formatMoney(price * 100);
      this.modalPrice.innerHTML = comparePrice
        ? `<s>${comparePrice.textContent}</s> ${priceText}`
        : priceText;
    }
    if (this.modalDescription) this.modalDescription.innerHTML = description;
    if (this.modalImages) {
      this.modalImages.innerHTML = images.length
        ? images.map(src => `<img src="${src}" alt="${title}" loading="lazy">`).join('')
        : '<div style="padding: 40px; text-align: center; opacity: 0.5;">No images available</div>';
    }

    // Configure add button
    if (this.modalAddBtn) {
      this.modalAddBtn.disabled = !isAvailable || cantFit;
      if (!isAvailable) {
        this.modalAddBtn.textContent = 'Sold Out';
      } else if (cantFit) {
        this.modalAddBtn.textContent = "Won't fit";
      } else {
        this.modalAddBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add to Box
        `;
      }

      // Remove previous listener and add new one
      const newAddBtn = this.modalAddBtn.cloneNode(true);
      this.modalAddBtn.parentNode.replaceChild(newAddBtn, this.modalAddBtn);
      this.modalAddBtn = newAddBtn;

      newAddBtn.addEventListener('click', () => {
        if (!isAvailable || cantFit) return;
        const productData = this.getProductDataFromCard(card);
        this.addToBundle(productData);
        this.closeProductModal();
      });
    }

    this.productModal.showModal();
  }

  closeProductModal() {
    if (!this.productModal) return;
    this.productModal.close();
  }

  async addBundleToCart(options = {}) {
    const { openDrawer = true } = options;
    const hasClosedBoxes = this.closedBoxes.length > 0;
    const currentBoxValid = this.getTotalSpotsUsed() >= this.minSpots;

    if (!hasClosedBoxes && !currentBoxValid) return;

    this.isLoading = true;
    if (this.addToCartBtn) {
      this.addToCartBtn.dataset.loading = 'true';
      this.addToCartBtn.textContent = 'Adding...';
    }

    try {
      // Collect items from all closed boxes
      const items = [];

      this.closedBoxes.forEach((box, boxIndex) => {
        box.products.forEach((product, itemIndex) => {
          items.push({
            id: parseInt(product.id),
            quantity: 1,
            properties: {
              '_bundle_type': this.bundleType,
              '_bundle_id': box.id,
              '_bundle_box': `Box ${boxIndex + 1}`,
              '_bundle_position': `Item ${itemIndex + 1} (${product.gridSize} spot${product.gridSize !== 1 ? 's' : ''})`,
              '_bundle_name': this.bundleName,
              '_bundle_grid_size': product.gridSize.toString()
            }
          });
        });
      });

      // Add current box items if valid
      if (currentBoxValid) {
        const currentBoxNumber = this.closedBoxes.length + 1;
        this.products.forEach((product, itemIndex) => {
          items.push({
            id: parseInt(product.id),
            quantity: 1,
            properties: {
              '_bundle_type': this.bundleType,
              '_bundle_id': this.bundleId,
              '_bundle_box': `Box ${currentBoxNumber}`,
              '_bundle_position': `Item ${itemIndex + 1} (${product.gridSize} spot${product.gridSize !== 1 ? 's' : ''})`,
              '_bundle_name': this.bundleName,
              '_bundle_grid_size': product.gridSize.toString()
            }
          });
        });
      }

      // Collect section IDs for cart drawer refresh
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];
      cartItemsComponents.forEach(item => {
        if (item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(',')
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Failed to add bundle to cart');
      }

      const responseData = await response.json();
      this.clearBundle();

      // Fetch updated cart
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      // Dispatch cart:update event
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'bundle-visual',
          data: {
            source: 'bundle-visual',
            itemCount: cart.item_count,
            sections: responseData.sections || {}
          }
        }
      }));

      // Open cart drawer (unless skipped)
      if (openDrawer) {
        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
          cartDrawer.showDialog();
        } else if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      this.showToast((error.message || 'There was an error adding the bundle to your cart.') + ' Please refresh and try again.', 5000);
    } finally {
      this.isLoading = false;
      if (this.addToCartBtn) {
        this.addToCartBtn.dataset.loading = 'false';
        this.updateUI();
      }
    }
  }

  addAnotherBox() {
    // Close current box and start a new one
    if (this.products.length === 0) return;

    const spotsUsed = this.getTotalSpotsUsed();
    if (spotsUsed < this.minSpots) {
      this.showToast(`Please fill at least ${this.minSpots} spots before adding another box`);
      return;
    }

    // Save current box to closed boxes
    this.closedBoxes.push({
      id: this.bundleId,
      products: [...this.products],
      gridState: JSON.parse(JSON.stringify(this.gridState))
    });

    // Reset current box
    this.products = [];
    this.initializeGrid();
    this.bundleId = this.generateBundleId();

    // Update UI
    this.renderClosedBoxes();
    this.updateBoxNumber();
    this.saveToStorage();
    this.updateUI();
  }

  removeClosedBox(boxId) {
    this.closedBoxes = this.closedBoxes.filter(box => box.id !== boxId);
    this.renderClosedBoxes();
    this.updateBoxNumber();
    this.saveToStorage();
    this.updateUI();
  }

  renderClosedBoxes() {
    if (!this.closedBoxesContainer) return;

    this.closedBoxesContainer.innerHTML = '';

    this.closedBoxes.forEach((box, index) => {
      const boxEl = document.createElement('div');
      boxEl.className = 'bundle-visual--closed-box';
      boxEl.dataset.boxId = box.id;

      // Create header
      const header = document.createElement('div');
      header.className = 'bundle-visual--closed-box-header';
      header.innerHTML = `
        <div class="bundle-visual--closed-box-label">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          Box ${index + 1}
        </div>
        <button type="button" class="bundle-visual--closed-box-remove" data-remove-box="${box.id}">Remove</button>
      `;

      // Create box visual
      const boxWrapper = document.createElement('div');
      boxWrapper.className = 'bundle-visual--box';
      boxWrapper.innerHTML = `
        <div class="bundle-visual--box-inner">
          <div class="bundle-visual--box-grid" style="--grid-columns: ${this.gridColumns}; --grid-rows: ${this.gridRows};">
            ${this.renderClosedBoxSpots(box)}
          </div>
          <div class="bundle-visual--items-layer" style="--grid-columns: ${this.gridColumns}; --grid-rows: ${this.gridRows};">
            ${this.renderClosedBoxItems(box)}
          </div>
        </div>
      `;

      boxEl.appendChild(header);
      boxEl.appendChild(boxWrapper);
      this.closedBoxesContainer.appendChild(boxEl);

      // Bind remove button
      const removeBtn = boxEl.querySelector('[data-remove-box]');
      removeBtn.addEventListener('click', () => this.removeClosedBox(box.id));
    });
  }

  renderClosedBoxSpots(box) {
    let html = '';
    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col < this.gridColumns; col++) {
        const isOccupied = box.gridState[row] && box.gridState[row][col] !== null;
        html += `<div class="bundle-visual--spot" data-occupied="${isOccupied}">
          <div class="bundle-visual--spot-empty">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
          </div>
        </div>`;
      }
    }
    return html;
  }

  renderClosedBoxItems(box) {
    let html = '';
    box.products.forEach(product => {
      const row = product.position.row + 1;
      const colStart = product.position.col + 1;
      let gridStyle = '';

      if (product.gridSize === 4) {
        gridStyle = `grid-row: ${row} / span 2; grid-column: ${colStart} / span 2;`;
      } else {
        gridStyle = `grid-row: ${row}; grid-column: ${colStart} / span ${product.gridSize};`;
      }

      html += `<div class="bundle-visual--item" data-size="${product.gridSize}" style="${gridStyle}">
        ${product.image ? `<img class="bundle-visual--item-image" src="${product.image}" alt="${product.title}">` : ''}
        <div class="bundle-visual--item-title">${product.title}</div>
      </div>`;
    });
    return html;
  }

  updateBoxNumber() {
    if (this.boxNumberSpan) {
      this.boxNumberSpan.textContent = this.closedBoxes.length + 1;
    }
  }

  saveToStorage() {
    try {
      const data = {
        products: this.products,
        gridState: this.gridState,
        closedBoxes: this.closedBoxes,
        bundleId: this.bundleId,
        bundleType: this.bundleType,
        timestamp: Date.now()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (e) {
      console.warn('Could not save bundle to localStorage:', e);
    }
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return;
      const data = JSON.parse(stored);
      const maxAge = 24 * 60 * 60 * 1000;
      if ((Date.now() - data.timestamp) >= maxAge) return;

      const validVariantIds = new Set(
        Array.from(this.productCards).map(c => c.dataset.variantId)
      );
      const validProducts = (data.products || []).filter(p => validVariantIds.has(p.id));

      this.closedBoxes = data.closedBoxes || [];
      this.bundleId = data.bundleId || this.generateBundleId();

      const packed = this.repack(validProducts);
      if (packed.failed) {
        this.products = [];
        this.initializeGrid();
      } else {
        this.products = validProducts;
        this._applyPack(packed);
      }

      if (validProducts.length !== (data.products || []).length) {
        this.saveToStorage();
      }

      this.renderClosedBoxes();
      this.updateBoxNumber();
    } catch (e) {
      console.warn('Could not load bundle from localStorage:', e);
    }
  }

  clearStorage() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      console.warn('Could not clear bundle from localStorage:', e);
    }
  }

  // Mobile Sheet Methods
  initMobileSheet() {
    if (!this.mobileSheet) return;
    this.bindMobileEvents();
  }

  bindMobileEvents() {
    if (this.mobileBar) {
      this.mobileBar.addEventListener('click', () => this.openMobilePanel());
    }

    if (this.mobileCloseBtn) {
      this.mobileCloseBtn.addEventListener('click', () => this.closeMobilePanel());
    }

    if (this.mobilePanel) {
      this.mobilePanel.addEventListener('click', (e) => {
        if (e.target === this.mobilePanel) {
          this.closeMobilePanel();
        }
      });

      this.mobilePanel.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.closeMobilePanel();
      });
    }

    if (this.mobileAddBtn) {
      this.mobileAddBtn.addEventListener('click', () => {
        this.closeMobilePanel();
        this.addBundleToCart();
      });
    }

    this.setupSwipeToClose();
  }

  setupSwipeToClose() {
    if (!this.mobilePanel) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    const header = this.mobilePanel.querySelector('.bundle-visual--mobile-panel-header');
    if (!header) return;

    header.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
      isDragging = true;
    }, { passive: true });

    header.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      if (deltaY > 0) {
        this.mobilePanel.style.transform = `translateY(${deltaY}px)`;
      }
    }, { passive: true });

    header.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      const deltaY = currentY - startY;
      this.mobilePanel.style.transform = '';
      if (deltaY > 80) {
        this.closeMobilePanel();
      }
    });
  }

  openMobilePanel() {
    if (!this.mobilePanel || this.isPanelOpen) return;

    this.isPanelOpen = true;
    this.mobileBar.setAttribute('aria-expanded', 'true');
    this.mobilePanel.showModal();
    document.body.style.overflow = 'hidden';
    this.syncMobilePanel();
  }

  closeMobilePanel() {
    if (!this.mobilePanel || !this.isPanelOpen) return;

    this.mobilePanel.classList.add('closing');

    this.mobilePanel.addEventListener('animationend', () => {
      this.mobilePanel.classList.remove('closing');
      this.mobilePanel.close();
      this.isPanelOpen = false;
      this.mobileBar.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }, { once: true });
  }

  syncMobilePanel() {
    if (!this.mobileItemsLayer) return;

    // Render items in mobile grid
    this.mobileItemsLayer.innerHTML = '';
    this.products.forEach(product => {
      const item = document.createElement('div');
      item.className = 'bundle-visual--item bundle-visual--item-mobile';
      item.dataset.internalId = product.internalId;
      item.dataset.size = product.gridSize;
      item.dataset.armed = 'false';

      const row = product.position.row + 1;
      const colStart = product.position.col + 1;
      const colSpan = product.gridSize >= this.gridColumns ? this.gridColumns : product.gridSize;

      item.style.gridRow = row;
      item.style.gridColumn = `${colStart} / span ${colSpan}`;

      item.innerHTML = `
        ${product.image ? `<img class="bundle-visual--item-image" src="${product.image}" alt="${product.title}">` : ''}
        <span class="bundle-visual--item-remove-overlay" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </span>
      `;

      item.addEventListener('click', () => {
        if (item.dataset.armed === 'true') {
          this.removeProduct(product.internalId);
          this.syncMobilePanel();
          return;
        }

        this.mobileItemsLayer.querySelectorAll('.bundle-visual--item-mobile[data-armed="true"]').forEach(other => {
          other.dataset.armed = 'false';
        });

        item.dataset.armed = 'true';
      });

      this.mobileItemsLayer.appendChild(item);
    });

    // Update mobile grid cells
    const mobileSpots = this.mobileGrid?.querySelectorAll('[data-spot-index]');
    if (mobileSpots) {
      mobileSpots.forEach(spot => {
        const row = parseInt(spot.dataset.row);
        const col = parseInt(spot.dataset.col);
        const isOccupied = this.gridState[row] && this.gridState[row][col] !== null;
        spot.dataset.occupied = isOccupied ? 'true' : 'false';
      });
    }
  }

  updateMobileUI() {
    if (!this.mobileSheet) return;

    const cellsUsed = this.getTotalSpotsUsed();
    const meetsMinimum = cellsUsed >= this.minSpots;
    const { discountedTotal, totalDiscount } = this.getDiscountedTotals();

    // Update mini preview spots
    if (this.mobileSpots) {
      this.mobileSpots.forEach((spot, index) => {
        const row = Math.floor(index / this.gridColumns);
        const col = index % this.gridColumns;
        const isOccupied = this.gridState[row] && this.gridState[row][col] !== null;
        spot.dataset.filled = isOccupied ? 'true' : 'false';
      });
    }

    // Update bar info
    if (this.mobileCount) {
      this.mobileCount.textContent = `${cellsUsed} of ${this.minSpots} spots filled`;
    }

    if (this.mobileBar) {
      this.mobileBar.dataset.complete = meetsMinimum ? 'true' : 'false';
    }

    if (this.mobilePrice) {
      this.mobilePrice.textContent = this.formatMoney(discountedTotal * 100);
    }

    // Update panel progress
    if (this.mobileProgressText) {
      if (cellsUsed === 0) {
        this.mobileProgressText.textContent = `Fill at least ${this.minSpots} spots`;
      } else if (meetsMinimum) {
        const remaining = this.maxSpots - cellsUsed;
        this.mobileProgressText.textContent = `${cellsUsed} spots filled${remaining > 0 ? ` (${remaining} remaining)` : ''}`;
      } else {
        this.mobileProgressText.textContent = `${cellsUsed} of ${this.minSpots} minimum spots filled`;
      }
    }

    // Update panel total price
    if (this.mobileTotalPrice) {
      this.mobileTotalPrice.textContent = this.formatMoney(discountedTotal * 100);
    }

    // Update mobile savings
    if (this.mobileSavings) {
      if (totalDiscount > 0) {
        this.mobileSavings.textContent = `You save ${this.formatMoney(totalDiscount * 100)}`;
        this.mobileSavings.style.display = '';
      } else {
        this.mobileSavings.style.display = 'none';
      }
    }

    // Update panel add button
    if (this.mobileAddBtn) {
      this.mobileAddBtn.disabled = !meetsMinimum;
      if (meetsMinimum) {
        this.mobileAddBtn.textContent = `Add Bundle to Cart (${this.products.length} items)`;
      } else {
        const cellsNeeded = this.minSpots - cellsUsed;
        this.mobileAddBtn.textContent = `Fill ${cellsNeeded} more spot${cellsNeeded !== 1 ? 's' : ''}`;
      }
    }

    // Sync panel if open
    if (this.isPanelOpen) {
      this.syncMobilePanel();
    }
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get('bundle-visual-element')) {
  customElements.define('bundle-visual-element', BundleVisualElement);
}
